import React, { useMemo } from 'react';

import type { UUID } from '@aline/common';
import { getIsWorkflowPhased } from '@aline/common';
import { getAuthorTrackedChangeCount } from '@aline/common/slate-api/queries/author-tracked-changes';
import { link, params } from '@aline/routes';
import { Typography } from '@mui/material';
import Color from 'color';
import { ArrowLeft, CircleCheck, ClipboardCheck } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import type { Editor } from 'slate';
import { AlineButton, AlineTooltip } from 'src/components';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';
import { useGetTaskWithComments } from 'src/hooks';
import {
  useDocContentChangedCount,
  useMaybeEditor,
  useSuggestionContentChangedCount,
} from 'src/layout-engine';
import { getIsSigningPhaseType } from 'src/workflow/helpers';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';

import { FOOTER_ACTION_ICON } from './footer-action-icon';
import { getReReviewReviewers, RequestReReviewButton } from './RequestReReviewButton';
import type { FooterAction, TaskDecisionParam } from './task-review-helpers';
import {
  FOOTER_ACTION,
  FOOTER_ACTION_LABEL,
  getApproveBlockedReason,
  getDecisionParamForAction,
  getFooterActionBlockedReason,
  getFooterActions,
  getFooterCompleteBlockedReason,
  getPrimaryFooterAction,
  getRequesterAssignment,
  getTaskDecisionLink,
  getTaskIsOpen,
  RE_REVIEW_FILL,
} from './task-review-helpers';
import { TaskReviewStatusPill } from './TaskStatusPill';
import { useUnsubmittedReview } from './use-unsubmitted-review';

const DONE_GREEN = '#12B066';

const rise = keyframes({
  from: { opacity: 0, transform: 'translate(-50%, 12px)' },
  to: { opacity: 1, transform: 'translate(-50%, 0)' },
});

const useStyles = makeStyles()(theme => ({
  actions: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  badge: {
    alignItems: 'center',
    backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    borderRadius: 8,
    color: theme.palette.primary.main,
    display: 'flex',
    flexShrink: 0,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  badgeDone: {
    backgroundColor: Color(DONE_GREEN).alpha(0.12).toString(),
    color: DONE_GREEN,
  },
  button: {
    borderRadius: 8,
    height: 36,
    whiteSpace: 'nowrap',
  },
  divider: {
    alignSelf: 'stretch',
    backgroundColor: theme.appColors.ruleSubtle,
    flexShrink: 0,
    margin: theme.spacing(0.5, 0),
    width: 1,
  },
  dock: {
    alignItems: 'center',
    animation: `${rise} 220ms ${theme.transitions.easing.easeOut}`,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 12,
    bottom: theme.spacing(3),
    boxShadow: '0 12px 32px rgba(16, 24, 40, 0.14), 0 2px 6px rgba(16, 24, 40, 0.06)',
    display: 'flex',
    gap: theme.spacing(1.5),
    left: '50%',
    maxWidth: `calc(100% - ${theme.spacing(4)})`,
    padding: theme.spacing(1.25, 1.25, 1.25, 1.5),
    position: 'absolute',
    transform: 'translateX(-50%)',
    zIndex: theme.zIndex.appBar,
  },
  heading: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  summary: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.25),
    minWidth: 0,
  },
  title: {
    color: theme.appColors.strongType,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '20px',
    maxWidth: 280,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

/** The review task a document was opened for (`?taskId=`), or null outside a review. */
export function useReviewTaskIdParam(): UUID | null {
  const location = useLocation();
  return new URLSearchParams(location.search).get(params.reviewTaskId);
}

/**
 * Floats over a document opened from a review task: which review it belongs to, and the way back
 * to the task page with the viewer's actions there. Shows `fallback` when the task turns out to be
 * unavailable, so a stale `?taskId=` does not hide the document's own alert.
 */
export function DocumentTaskReviewDock({
  fallback,
  taskId,
}: {
  fallback: React.ReactNode;
  taskId: UUID;
}): JSX.Element | null {
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const { loading, task } = useGetTaskWithComments(taskId);
  const viewer = task ? getRequesterAssignment(task.assignments, currentUserId) : null;
  const liveTrackedChangeCount = useOwnTrackedChangeCount(viewer?.reviewRequestedAt ?? null);
  const isExternalView = Boolean(task && !task.requesterIsInternal);
  const footerActions = task
    ? getFooterActions({
        canEdit: !task.requesterDisallowedReason.edit && !isExternalView,
        isInternal: !isExternalView,
        isOpen: getTaskIsOpen(task),
        isPhaseActive: Boolean(task.phase.startedAt) && !task.phase.completedAt,
        isPhased: getIsWorkflowPhased(task.workflow.phases, getIsSigningPhaseType),
        isStarted: Boolean(task.startedAt),
        task,
        viewer,
      })
    : [];
  const { review } = useUnsubmittedReview(taskId, {
    skip: !footerActions.includes(FOOTER_ACTION.Approve),
  });
  if (loading) return null;
  if (!task) return <>{fallback}</>;

  const completeBlockedReason = footerActions.includes(FOOTER_ACTION.Complete)
    ? getFooterCompleteBlockedReason({
        assignments: task.assignments,
        isExternalView,
        viewer,
      })
    : null;
  const approveBlockedReason = footerActions.includes(FOOTER_ACTION.Approve)
    ? getApproveBlockedReason(review, { liveTrackedChangeCount })
    : null;
  const isAssigneeViewer = getTaskIsOpen(task) && viewer?.role === Apollo.TaskAssigneeRole.Assignee;
  const reReviewReviewers = isAssigneeViewer ? getReReviewReviewers(task.assignments) : [];
  const primaryAction = getPrimaryFooterAction({
    actions: footerActions,
    hasReReview: reReviewReviewers.length > 0,
    isCompleteBlocked: Boolean(completeBlockedReason),
  });
  // The primary action is first in the footer and sits last, nearest the edge, in the dock.
  const actions = footerActions
    .flatMap(action => {
      const decision = getDecisionParamForAction(action);
      return decision ? [{ action, decision, isPrimary: action === primaryAction }] : [];
    })
    .toReversed();
  const hasDecided = viewer?.decision === Apollo.TaskAssigneeDecision.Completed;
  const changeStatusDisabledReason = task.requesterDisallowedReason.changeStatus ?? null;
  const handOff = (decision: TaskDecisionParam) =>
    openLink(getTaskDecisionLink(task.id, decision), {
      applyRouteToExistingTab: true,
      title: task.title,
    });

  return (
    <div aria-label="Review task" className={classes.dock} role="region">
      <div className={classes.summary}>
        <DockBadge isDone={hasDecided} />
        <div className={classes.heading}>
          <Typography className={classes.title} title={task.title}>
            {task.title || 'Untitled task'}
          </Typography>
          <TaskReviewStatusPill status={task.status} />
        </div>
      </div>
      <div className={classes.divider} />
      <div className={classes.actions}>
        <AlineButton
          className={classes.button}
          color="primary"
          startIcon={<ArrowLeft size={16} />}
          variant="text"
          onClick={() => openLink(link.TaskLanding({ taskId: task.id }), { title: task.title })}
        >
          Back to Review
        </AlineButton>
        {actions.map(({ action, decision, isPrimary }) => (
          <React.Fragment key={action}>
            {action === FOOTER_ACTION.Complete && reReviewReviewers.length > 0 && (
              <RequestReReviewButton
                isBar
                className={classes.button}
                disabledReason={changeStatusDisabledReason}
                isPrimary={primaryAction === RE_REVIEW_FILL}
                reviewers={reReviewReviewers}
                taskId={task.id}
              />
            )}
            <DockButton
              action={action}
              disabledReason={
                changeStatusDisabledReason ??
                getFooterActionBlockedReason(action, {
                  approveBlockedReason,
                  completeBlockedReason,
                })
              }
              variant={isPrimary ? 'contained' : 'outlined'}
              onClick={() => handOff(decision)}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function DockButton({
  action,
  disabledReason,
  onClick,
  variant,
}: {
  action: FooterAction;
  disabledReason: string | null;
  onClick: () => void;
  variant: 'contained' | 'outlined';
}): JSX.Element {
  const { classes } = useStyles();
  const Icon = FOOTER_ACTION_ICON[action];
  return (
    <AlineTooltip arrow placement="top" title={disabledReason ?? ''}>
      <span>
        <AlineButton
          className={classes.button}
          color="primary"
          disabled={Boolean(disabledReason)}
          startIcon={<Icon size={16} />}
          variant={variant}
          onClick={onClick}
        >
          {FOOTER_ACTION_LABEL[action]}
        </AlineButton>
      </span>
    </AlineTooltip>
  );
}

function DockBadge({ isDone }: { isDone: boolean }): JSX.Element {
  const { classes, cx } = useStyles();
  const Icon = isDone ? CircleCheck : ClipboardCheck;
  return (
    <div className={cx(classes.badge, isDone && classes.badgeDone)}>
      <Icon size={18} />
    </div>
  );
}

/**
 * The viewer's tracked changes in the open document since they were asked to review, recounted as
 * they type, so Approve locks the moment they redline instead of after the next save.
 */
function useOwnTrackedChangeCount(since: string | null): number {
  const editor = useMaybeEditor();
  const currentUserId = useCurrentUserId();
  const { docContentChangedCount } = useDocContentChangedCount();
  const { suggestionContentChangedCount } = useSuggestionContentChangedCount();
  return useMemo(
    () =>
      countOwnTrackedChanges(
        { currentUserId, editor, since },
        docContentChangedCount,
        suggestionContentChangedCount
      ),
    [currentUserId, docContentChangedCount, editor, since, suggestionContentChangedCount]
  );
}

// The change counters are read only as memo dependencies, so the count reruns as the doc changes.
function countOwnTrackedChanges(
  options: { currentUserId: string | null; editor: Editor | null; since: string | null },
  _docContentChangedCount: number,
  _suggestionContentChangedCount: number
): number {
  const { currentUserId, editor, since } = options;
  if (!editor || !currentUserId || !since) return 0;
  return getAuthorTrackedChangeCount(editor, { authorId: currentUserId, since });
}
